import { useState } from "react";
import { precios } from "@/mocks/precios";

interface Props {
  ids?: string[];
  className?: string;
}

const MOBILE_VISIBLE = 3;

export default function PricingSection({ ids, className }: Props) {
  const [expanded, setExpanded] = useState(false);
  const items = ids
    ? ids.map((id) => precios.find((p) => p.id === id)).filter(Boolean) as typeof precios
    : precios;

  const visibleItems = expanded ? items : items.slice(0, MOBILE_VISIBLE);
  const hasMore = items.length > MOBILE_VISIBLE;

  const offerSchema = {
    "@context": "https://schema.org",
    "@type": "ItemList",
    "itemListElement": items.map((item, i) => ({
      "@type": "ListItem",
      "position": i + 1,
      "item": {
        "@type": "Offer",
        "name": item.title,
        "price": item.price.replace("desde ", "").replace(" €", ""),
        "priceCurrency": "EUR",
        "description": item.desc,
        "offeredBy": {
          "@type": "LocalBusiness",
          "name": "Persianas Málaga"
        }
      }
    }))
  };

  return (
    <section className={className || "py-12 md:py-16 bg-background-50"}>
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(offerSchema) }} />
      <div className="px-4 md:px-6 max-w-7xl mx-auto">
        <div className="text-center mb-8 md:mb-12">
          <span className="inline-block text-xs font-semibold tracking-widest uppercase text-primary-600 bg-primary-50/70 px-3 py-1 rounded-full mb-4">
            Precios orientativos
          </span>
          <h2 className="text-2xl md:text-3xl font-bold text-foreground-950 mb-3 font-heading tracking-tight">
            Tarifas de reparación e instalación
          </h2>
          <p className="text-sm md:text-base text-foreground-600 max-w-xl mx-auto leading-relaxed">
            Precios «desde», orientativos. El presupuesto exacto y cerrado te lo damos gratis tras revisar tu caso en persona, sin compromiso.
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
          {visibleItems.map((item) => (
            <div
              key={item.id}
              className="group bg-background-50 rounded-2xl p-5 md:p-6 border border-background-200/70 hover:border-background-300 transition-all duration-300 hover:-translate-y-1 flex flex-col"
            >
              <div className="w-9 h-9 flex items-center justify-center rounded-lg bg-secondary-100 text-secondary-600 mb-4 group-hover:bg-secondary-200 transition-colors">
                <span className="w-4 h-4 flex items-center justify-center">
                  <i className={`${item.icon} text-base`}></i>
                </span>
              </div>

              <h3 className="text-sm md:text-base font-semibold text-foreground-900 mb-2 leading-snug">
                {item.title}
              </h3>

              <p className="text-xs md:text-sm text-foreground-600 leading-relaxed mb-5 flex-1">
                {item.desc}
              </p>

              <div className="mb-5">
                <div className="text-2xl md:text-3xl font-bold font-heading text-foreground-950 tracking-tight">
                  {item.price}
                </div>
                <span className="text-xs text-foreground-400 mt-1 block">
                  Presupuesto gratis sin compromiso
                </span>
              </div>

              <a
                href="tel:+34951120770"
                className="inline-flex items-center justify-center gap-2 bg-secondary-500 hover:bg-secondary-600 text-background-50 font-semibold text-xs md:text-sm px-5 py-3 rounded-full transition-all active:scale-[0.98] whitespace-nowrap cursor-pointer w-full"
              >
                <span className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-phone-fill"></i>
                </span>
                Pedir presupuesto
              </a>
            </div>
          ))}
        </div>

        {hasMore && (
          <div className="mt-5 text-center">
            <button
              onClick={() => setExpanded(!expanded)}
              className="inline-flex items-center gap-2 px-6 py-3 bg-background-100 hover:bg-background-200 text-foreground-700 font-semibold text-sm rounded-full border border-background-200 transition-all cursor-pointer"
            >
              {expanded ? (
                <>Ver menos <span className="w-4 h-4 flex items-center justify-center"><i className="ri-arrow-up-s-line"></i></span></>
              ) : (
                <>Ver los {items.length} precios <span className="w-4 h-4 flex items-center justify-center"><i className="ri-arrow-down-s-line"></i></span></>
              )}
            </button>
          </div>
        )}

        <p className="text-xs text-foreground-400 text-center mt-6">
          * Precios «desde» orientativos, IVA incluido. El presupuesto definitivo se entrega gratis tras la revisión en persona, sin compromiso.
        </p>
      </div>
    </section>
  );
}